/**
 * transitionEnd wrapper
 */
export default ((window) => {
  'use strict';

  let eventName;
  const durationPadding = 5;
  
  /**
   * Get transition-duration CSS value from a specified element.
   * Returns 0 if undefined.
   * If the specified element has multiple transitions, return the first one.
   * 
   * @param {HTMLElement} element 
   * @returns {Float}
   */
  const getTransitionDurationFromElement = element => {
    if (!element) {
      return 0;
    }

    // get transition-duration of the element
    let { transitionDuration, transitionDelay } = window.getComputedStyle(element);

    const floatTransitionDuration = Number.parseFloat(transitionDuration);
    const floatTransitionDelay = Number.parseFloat(transitionDelay);

    // return 0 if element or transition duration is not found
    if (!floatTransitionDuration && !floatTransitionDelay) {
      return 0;
    }

    // of multiple durations are defined, take the first
    transitionDuration = transitionDuration.split(',')[0];
    transitionDelay = transitionDelay.split(',')[0];

    return (Number.parseFloat(transitionDuration) + Number.parseFloat(transitionDelay)) * 1000;
  };

  /**
   * Determine transitionEnd event name.
   * 
   * @returns String
   */
  const getTransitionEndEventName = () => {
    // get from cache
    if (eventName !== undefined) {
      return eventName;
    }

    eventName = '';

    try {
      const transitions = {
        transition: 'transitionend',
        WebkitTransition: 'webkitTransitionEnd',
        MozTransition: 'transitionend',
        OTransition: 'oTransitionEnd otransitionend',
      };

      for (let t in transitions) {
        if (window.document.body.style[t] !== undefined) {
          eventName = transitions[t];
          break;
        }
      }
    }
    catch (err) {
      eventName = '';
    }

    return eventName;
  };


  /**
   * TransitionEnd class.
   * Wrap functionality and provide method to 'unbind' the event.
   */
  class TransitionEnd {
    constructor(element, callback, fallbackDuration) {
      this.element = element;
      this.callback = callback;
      this.fallbackDuration = Number.parseFloat(fallbackDuration) || 0;
      this.eventName = getTransitionEndEventName();

      this.fallbackTimeout = null;
      this.handled = false;
      this.handler = this._handler.bind(this);

      this.listen();
      
    }

    /**
     * Execute callback.
     */
    _handler() {
      // bail if the callback is already executed or has been aborted.
      if (this.handled) {
        this.cleanUp();
        return;
      }

      // run the callback asynchronously
      (async (callback) => {
        if (typeof callback === 'function') {
          callback.apply(this.element);
        }
      })(this.callback);

      // clean up
      this.cleanUp();
    }

    /**
     * Setup the event
     */
    listen() {
      let duration;

      try {
        duration = getTransitionDurationFromElement(this.element);
      }
      catch (err) {
        duration = this.fallbackDuration;
      }

      duration += durationPadding;

      // try addEventListener
      try {
        if (this.eventName) {
          this.element.addEventListener(this.eventName, this.handler, false);
        }
      } catch (err) {
        // ignore
      }

      // ensure the callback is always called
      this.fallbackTimeout = window.setTimeout(() => {
        // try dispatchEvent
        try {
          if (this.eventName && window.Event) {
            this.element.dispatchEvent(new window.Event(this.eventName));
          }
        } catch (err) {
          // otherwise, just execute the callback
          this.handler();
        }
      }, duration);
    }

    /**
     * Clean up
     */
    cleanUp() {
      this.handled = true;

      if (this.handler) {
        try {
          this.element.removeEventListener(this.eventName, this.handler);
        }
        catch (err) {
          // ignore
        }

        this.handler = null;
      }

      if (this.fallbackTimeout) {
        window.clearTimeout(this.fallbackTimeout);
        this.fallbackTimeout = null;
      }
    }

    /**
     * Abort
     */
    cancel() {
      this.cleanUp();
    }
  }

  return (element, callback, fallbackDuration) => {
    const el = element[0] || element;
    const inst = new TransitionEnd(el, callback, fallbackDuration);
    return {
      abort: inst.cancel.bind(inst)
    };
  };
})(window);